import { AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartData, ChartOptions, ChartType, CoreChartOptions, DefaultDataPoint, registerables } from 'chart.js';
import { merge } from 'lodash-es';
import 'chartjs-adapter-date-fns';

// TODO: only import and register required components, to improve treeshaking
Chart.register(...registerables);

@Component({
  selector: 'qm-chart-js',
  templateUrl: './chart-js.component.html',
})
export class ChartJsComponent<
  TType extends ChartType,
  TData = DefaultDataPoint<TType>,
  TLabel = unknown,
> implements AfterViewInit, OnDestroy, OnChanges {
  @Input()
  public type: TType;
  @Input()
  public data: ChartData<TType, TData, TLabel>;
  @Input()
  public options?: ChartOptions<TType>;

  private readonly defaultOptions: Partial<CoreChartOptions<TType>> = {
    responsive: true,
    maintainAspectRatio: false,
  };

  @ViewChild('canvas')
  public canvas: ElementRef<HTMLCanvasElement>;

  private chart: Chart<TType, TData, TLabel>;

  constructor(
    private readonly zone: NgZone,
  ) { }

  // need to wait for `canvas` to be available before init
  ngAfterViewInit(): void {
    this.initChart();
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // initial init from ngAfterViewInit not done yet
    if (!this.chart) {
      return;
    }

    if (changes.type) {
      this.initChart();
      return;
    }

    if (changes.data) {
      this.chart.data = this.data;
    }
    if (changes.options) {
      merge(this.chart.options, this.options);
    }

    this.zone.runOutsideAngular(() => {
      this.chart.update();
    });
  }

  private initChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.chart = new Chart(this.canvas.nativeElement, {
      type: this.type,
      data: this.data,
      options: merge(this.defaultOptions, this.options),
    });
  }
}
